const ALDERSTILPASSET = "ALDERSTILPASSET";
const AKSJER = "AKSJER";
const OFFENSIV = "OFFENSIV";
const MODERAT = "MODERAT";
const FORSIKTIG = "FORSIKTIG";
const RENTER = "RENTER";
const APEN = "APEN";
const LOFAVOR = "LOFAVOR";
const GARANTI = "GARANTI";

export const SPAREPROFILER = {
	ALDERSTILPASSET,
	AKSJER,
	OFFENSIV,
	MODERAT,
	FORSIKTIG,
	RENTER,
	APEN,
	LOFAVOR,
	GARANTI,
} as const;

export type Spareprofil = keyof typeof spareprofiler;

export type SpareprofilItem = {
	id: Spareprofil;
	name: string;
	sharePercentage?: number;
	nedtrapping?: boolean;
	nedtrappingAge?: number;
	nedtrappingFordeling?: readonly number[];
	description: string;
};

type SpareprofilerItem<T extends SpareprofilItem> = {
	id: T["id"];
	name: T["name"];
	sharePercentage: T["sharePercentage"] extends number ? T["sharePercentage"] : undefined;
	nedtrapping: T["nedtrapping"] extends boolean ? T["nedtrapping"] : undefined;
	nedtrappingAge: T["nedtrappingAge"] extends number ? T["nedtrappingAge"] : undefined;
	nedtrappingFordeling?: T["nedtrappingFordeling"] extends readonly number[] ? T["nedtrappingFordeling"] : undefined;
	description: T["description"];
};

type Spareprofiler = {
	[K in keyof typeof spareprofiler]: SpareprofilerItem<(typeof spareprofiler)[K]>;
};

export const AlderstilpassetName = "Alderstilpasset";
const AlderstilpassetDescription = "For deg som ønsker at risikoen skal tilpasses din alder og gradvis trappes ned mot pensjonsalder.";

const AksjerName = "Aksjer";
const AksjerDescription = "For deg som er opptatt av høy avkastning og er villig til å ta høy risiko.";

const OffensivName = "Offensiv";
const OffensivDescription = "For deg som er opptatt av avkastning og er villig til å ta en del risiko.";

const ModeratName = "Moderat";
const ModeratDescription = "For deg som er opptatt av trygghet og avkastning, og ønsker moderat risiko.";

const ForsiktigName = "Forsiktig";
const ForsiktigDescription = "For deg som er opptatt av trygghet og ønsker lav risiko.";

const RenterName = "Renter";
const RenterDescription = "For deg som er opptatt av trygghet og ønsker en så lav risiko som mulig.";

const LOfavorName = "LOfavør pensjonskapitalbevis";
const LOfavorDescription =
	"Jeg ønsker mulighet for høy forventet avkastning med store " +
	"svingninger som ung, og gradvis mindre risiko og svingninger når det " +
	"nærmer seg utbetaling. Sparingen tilpasses alderen min og andelen aksjer " +
	"reduseres gradvis 27 år før utbetaling. " +
	"LOfavør pensjonskapitalbevis har lavere forvaltningskostnader og årlig " +
	"avkastningsgaranti i utbetalingsperioden, senest fra fylte 67 år.";

const ApenName = "Fritt porteføljevalg";
const ApenDescription =
	"Passer for deg som vil sette sammen din egen portefølje basert på våre spareprofiler." +
	" Du velger selv sammensetning av aksjer og renter og aktiv, indeks og svanemerket forvaltning." +
	" Merk at automatisk nedtrapping ikke er tilgjengelig i fritt porteføljevalg.";

const GarantiName = "Garanti";
const GarantiDescription =
	"Pengene forvaltes av SpareBank\u00A01 i en kollektivportefølje " +
	"og plasseres i norske og internasjonale aksjer, eiendom og rentebærende " +
	"papirer. Ved negativ avkastning vil SpareBank\u00A01 tilføre en årlig innbetaling " +
	"til avtalen din. Det betyr at saldoen vil variere og at den kan ha " +
	"negativ utvikling i løpet av året.";

export function getSpareprofil(anbefaling: Spareprofil, valgt?: Spareprofil): SpareprofilItem {
	return spareprofiler[valgt ?? anbefaling];
}

const spareprofiler = {
	[ALDERSTILPASSET]: {
		id: ALDERSTILPASSET,
		name: AlderstilpassetName,
		sharePercentage: 100,
		nedtrapping: true,
		nedtrappingAge: 40,
		description: AlderstilpassetDescription,
	},
	[AKSJER]: {
		id: AKSJER,
		name: AksjerName,
		sharePercentage: 100,
		nedtrapping: true,
		nedtrappingAge: 57,
		nedtrappingFordeling: [100, 94, 88, 82, 76, 70, 64, 58, 52, 46, 40],
		description: AksjerDescription,
	},
	[OFFENSIV]: {
		id: OFFENSIV,
		name: OffensivName,
		sharePercentage: 75,
		nedtrapping: true,
		nedtrappingAge: 57,
		nedtrappingFordeling: [75, 71, 66, 62, 57, 53, 48, 44, 39, 35, 30],
		description: OffensivDescription,
	},
	[MODERAT]: {
		id: MODERAT,
		name: ModeratName,
		sharePercentage: 50,
		nedtrapping: true,
		nedtrappingAge: 57,
		nedtrappingFordeling: [50, 47, 44, 41, 38, 35, 32, 29, 26, 23, 20],
		description: ModeratDescription,
	},
	[FORSIKTIG]: {
		id: FORSIKTIG,
		name: ForsiktigName,
		sharePercentage: 25,
		nedtrapping: true,
		nedtrappingAge: 57,
		nedtrappingFordeling: [25, 24, 22, 21, 19, 18, 16, 15, 13, 12, 10],
		description: ForsiktigDescription,
	},
	[RENTER]: {
		id: RENTER,
		name: RenterName,
		sharePercentage: 0,
		nedtrapping: false,
		description: RenterDescription,
	},
	[LOFAVOR]: {
		id: LOFAVOR,
		name: LOfavorName,
		nedtrapping: true,
		nedtrappingAge: 40,
		description: LOfavorDescription,
	},
	[APEN]: {
		id: APEN,
		name: ApenName,
		description: ApenDescription,
	},
	[GARANTI]: {
		id: GARANTI,
		name: GarantiName,
		description: GarantiDescription,
	},
} as const;

export { ALDERSTILPASSET, AKSJER, OFFENSIV, MODERAT, FORSIKTIG, RENTER, APEN, LOFAVOR, GARANTI };

export default spareprofiler as Spareprofiler;
